import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { Admin } from '../types'
import { adminApi } from '../lib/api'

interface AuthState {
  admin: Admin | null
  token: string | null
  isAuthenticated: boolean
  twoFactorRequired: boolean
  tempToken: string | null
  isHydrated: boolean
  login: (username: string, password: string) => Promise<{ requiresTwoFactor: boolean; tempToken?: string }>
  verifyTwoFactor: (code: string, tempToken: string) => Promise<void>
  logout: () => void
  refreshAdmin: () => Promise<void>
  setHydrated: () => void
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      admin: null,
      token: null,
      isAuthenticated: false,
      twoFactorRequired: false,
      tempToken: null,
      isHydrated: false,

      setHydrated: () => set({ isHydrated: true }),

      login: async (username: string, password: string) => {
        try {
          const response = await adminApi.login(username, password)
          console.log('Login response:', response)
          
          let responseData = response.data
          
          // Handle different response structures
          if (!responseData) {
            throw new Error('No data received from server')
          }
          
          // Backend returns: { success: true, data: { admin: {...}, tokens: {...} } }
          const data = responseData.data || responseData

          if (data.requiresTwoFactor || data.requires_two_factor) {
            set({
              twoFactorRequired: true,
              tempToken: data.tempToken || data.temp_token,
            })
            return { requiresTwoFactor: true, tempToken: data.tempToken || data.temp_token }
          }

          const admin = data.admin
          const token = data.tokens?.access_token || data.token || data.access_token
          
          if (!admin || !token) {
            throw new Error('Invalid response: missing admin or token')
          }
          
          set({
            admin,
            token,
            isAuthenticated: true,
            twoFactorRequired: false,
            tempToken: null,
          })

          return { requiresTwoFactor: false }
        } catch (error: any) {
          console.error('Login error:', error)
          console.error('Error response data:', error.response?.data)
          
          // Extract user-friendly error message
          let errorMessage = 'Login failed'
          
          if (error.response?.data) {
            errorMessage = error.response.data.error || 
                          error.response.data.message || 
                          error.response.data.error_description ||
                          errorMessage
          } else if (error.message) {
            errorMessage = error.message
          }
          
          const enhancedError = new Error(errorMessage)
          // Preserve original error properties for debugging
          ;(enhancedError as any).originalError = error
          throw enhancedError
        }
      },

      verifyTwoFactor: async (code: string, tempToken: string) => {
        try {
          const response = await adminApi.verifyTwoFactor(code, tempToken)
          const responseData = response.data
          
          if (!responseData) {
            throw new Error('No data received from server')
          }
          
          const data = responseData.data || responseData
          
          if (!data.admin || !data.token) {
            throw new Error('Invalid response: missing admin or token')
          }

          set({
            admin: data.admin,
            token: data.token,
            isAuthenticated: true,
            twoFactorRequired: false,
            tempToken: null,
          })
        } catch (error) {
          console.error('2FA verification error:', error)
          throw error
        }
      },

      logout: () => {
        set({
          admin: null,
          token: null,
          isAuthenticated: false,
          twoFactorRequired: false,
          tempToken: null,
        })
      },

      refreshAdmin: async () => {
        const { token } = get()
        if (!token) return

        try {
          const response = await adminApi.getMe()
          const responseData = response.data
          
          if (!responseData) {
            throw new Error('No data received from server')
          }
          
          const admin = responseData.data || responseData
          
          if (!admin) {
            throw new Error('Invalid response: missing admin data')
          }
          
          set({ admin })
        } catch (error) {
          console.error('Failed to refresh admin:', error)
          get().logout()
        }
      },
    }),
    {
      name: 'waybook-admin-auth',
      partialize: (state) => ({
        admin: state.admin,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        state?.setHydrated()
      },
    }
  )
)
